import { useSnackbar } from 'notistack';
import React, { useCallback, useContext, useEffect, useReducer, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { timeStampReducer, UpdateFetchTimestamps } from '../../Reducers/timestampReducer';
import { fetchPostBackEnd, fetchGetBackEnd } from '../../tools/fetchRequests';
import {
  LogAdmin,
  TimestampStateObject,
} from '../../types/contexts/globalContext';
import { GetAllGarmentsResponse } from '../../types/routes/garments';
import { ResEnd, ResError } from '../../types/routes/general';
import { logAdminExternal } from '../../utils/logging';
import { getSecondsDifference } from '../../utils/timeFunctions';
import { LoggingContext } from '../LoggingContext/LoggingContext';
import {
  getInitialTimestampState,
  getIsAdmin,
  getIsLoggedIn,
  statusCodeMessageObject,
} from './globalContextFunctions';

interface GlobalContextInterface {
  filterMobileOpen: boolean;
  setFilterMobileOpen: React.Dispatch<React.SetStateAction<boolean>>;
  fetchGet: (urlString: string) => Promise<ResEnd>;
  fetchPost: (urlString: string, bodyPayload: unknown) => Promise<ResEnd>;
  logAdmin: LogAdmin;
  isLoggedIn: boolean;
  setIsLoggedIn: React.Dispatch<React.SetStateAction<boolean>>;
  setIsAdmin: React.Dispatch<React.SetStateAction<boolean>>;
  timestampsState: TimestampStateObject;
  dispatchTimestampState: React.Dispatch<UpdateFetchTimestamps>;

}
export const GlobalContext = React.createContext<GlobalContextInterface>(
  {} as GlobalContextInterface
);

export default function GlobalContextProvider(props: { children: React.ReactNode }) {
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  // USECONTEXT
  const { addLog } = useContext(LoggingContext);
  // USESTATE
  // Holds state to determine if the filter is open or closed (it's permanently open on none mobile view)
  const [filterMobileOpen, setFilterMobileOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(getIsAdmin());
  const [isLoggedIn, setIsLoggedIn] = useState(getIsLoggedIn());
  const [timestampsState, dispatchTimestampState] = useReducer(
    timeStampReducer,
    getInitialTimestampState()
  );

  const logAdmin = useCallback(
    (...args) => {
      if (isAdmin !== true) return;
      const logArgs = [...args, { viaContext: true }];
      logAdminExternal(...logArgs);
    },
    [isAdmin]
  );

  function handleBadFetchStatus(errorStatusCode: number): void {
    logAdmin({
      location: 'handleBadFetchStatus()',
      message: 'Error: Unauthorized access. Log in has expired. Please try logging in again.',
      statusCode: errorStatusCode,
    });
    enqueueSnackbar(
      `Fetch response error code: ${errorStatusCode}. ${
        statusCodeMessageObject[errorStatusCode] || ''
      }`,
      { variant: 'error' }
    );
  }

  const fetchPost = useCallback(
    async (urlString: string, bodyPayload: unknown): Promise<ResEnd | ResError> => {
      const stringifiedPayload = JSON.stringify(bodyPayload);
      const fetchResponse = await fetchPostBackEnd(urlString, stringifiedPayload, logAdmin);
      if (fetchResponse.status < 200 || fetchResponse.status > 299) {
        handleBadFetchStatus(fetchResponse.status);
        return { error: true, message: '401 - jwt token expired' };
      }
      const parsedResponse = await fetchResponse.json();
      return parsedResponse;
    },
    [logAdmin]
  );

  const fetchGet = useCallback(
    async (urlString: string): Promise<ResEnd> => {
      const fetchResponse = await fetchGetBackEnd(urlString, logAdmin);
      if (fetchResponse.status < 200 || fetchResponse.status > 299) {
        handleBadFetchStatus(fetchResponse.status);
        return { error: true, message: '401 - jwt token expired' };
      }

      const parsedResponse = await fetchResponse.json();
      return parsedResponse;
    },
    [logAdmin]
  );

  

  // Fetch garment arrays on load.
  useEffect(() => {
    // If we've already fetched teeTypes in the last 10 minutes then don't bother doing again
    const secondsSinceLastFetch = getSecondsDifference(
      timestampsState.fetch.newTeeTypes,
      new Date().toUTCString()
    );

    if (secondsSinceLastFetch !== -1 && secondsSinceLastFetch < 60 * 10) {
      return;
    }

    addLog('request sent for newteetypes', location.pathname);
    fetchGet('/measurements/garments/getallgarments')
      .then((responseUnchecked) => {
        if (responseUnchecked.error) {
          throw Error('Failed to fetch /measurements/garments/getallgarments');
        }
        const responseBody = responseUnchecked.body as GetAllGarmentsResponse;
        localStorage.setItem(
          'teeMeasuresAverages',
          JSON.stringify(responseBody.teeMeasuresAveragesRows)
        );
        localStorage.setItem('newTeeTypes', JSON.stringify(responseBody.newTeeTypesRows));
        localStorage.setItem('costslogarray', JSON.stringify(responseBody.garmentpriceslogArray));
        dispatchTimestampState({
          type: 'update fetch timestamps',
          payload: { labels: ['newTeeTypes', 'costslog'] },
        });
      })
      .catch(() => {
        enqueueSnackbar('Error fetch /measurements/garments/getallgarments', { variant: 'error' });
      });
  }, []);

  const value = React.useMemo(
    () => ({
      filterMobileOpen,
      setFilterMobileOpen,
      fetchGet,
      fetchPost,
      logAdmin,
      isLoggedIn,
      setIsLoggedIn,
      setIsAdmin,
      timestampsState,
      dispatchTimestampState,
    
    }),
    [filterMobileOpen, isLoggedIn, timestampsState, ]
  );

  return <GlobalContext.Provider value={value} {...props} />;
}
